import '@fortawesome/fontawesome-free/css/all.min.css';
import AdminLayout from 'cms/core/shared/AdminLayout';
import { HttpService } from 'core/services/http';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import { UserHttpService } from 'cms/features/feature_user/service';
import LastOnlineUsersWidget from 'cms/features/feature_user/components/LastOnlineUsersWidget';
import {
    getProfileAvatarUrl,
    getProfileDisplayName,
    getProfileAvatarLetter,
    nameToAvatarColor,
} from 'features/feature_job/utils/profileDisplay';
import {
    Avatar,
    Box,
    Chip,
    Divider,
    Grid,
    LinearProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper,
    Stack,
    Typography,
    Tabs,
    Tab
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { alpha } from '@mui/material/styles';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
    OverviewStatsChart,
    MonthlyJobsChart,
    InvoiceStatusChart,
    ApplicationsOverTimeChart
} from '../../../features/feature_user/components/DashboardCharts';

const DAY = 86400000;

/** Items created in the last 30 days vs the 30 days before, as a chip label + direction. */
function monthlyChange(items) {
    const now = Date.now();
    let recent = 0;
    let previous = 0;
    items.forEach((item) => {
        const t = new Date(item.created_at || item.profile?.created_at).getTime();
        if (Number.isNaN(t)) return;
        if (now - t <= 30 * DAY) recent += 1;
        else if (now - t <= 60 * DAY) previous += 1;
    });
    if (previous === 0) return { label: recent ? `+${recent} this month` : 'No change', down: false };
    const pct = Math.round(((recent - previous) / previous) * 100);
    return { label: `${pct >= 0 ? '+' : ''}${pct}%`, down: pct < 0 };
}

function Dashboard() {
    const user = HttpService.getUser?.();
    const [tabValue, setTabValue] = useState(0);

    // Load statistics data
    const { data: jobsData, isLoading: jobsLoading } = useQuery('dashboardJobs', () => JobHttpService.loadJobs({ size: 1000, filters: { archived: '0' } }));
    const { data: archivedData } = useQuery('dashboardArchivedJobs', () => JobHttpService.loadJobs({ size: 1, filters: { archived: '1' } }));
    const { data: applicationsData, isLoading: applicationsLoading } = useQuery('dashboardApplications', () => JobHttpService.loadJobApplications({ size: 1000 }));
    const { data: usersData, isLoading: usersLoading } = useQuery('dashboardUsers', () => UserHttpService.loadUsers());
    
    const jobs = jobsData?.results || [];
    const applications = applicationsData?.results || applicationsData || [];
    const users = usersData?.results || usersData || [];

    // Calculate statistics
    const newUsers = users.filter(u => {
        const created = new Date(u.created_at || u.profile?.created_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return created > weekAgo;
    });

    const activeJobs = jobsData?.count ?? jobs.length;
    const archivedJobs = archivedData?.count ?? 0;
    const applicationsTotal = applicationsData?.count ?? applications.length;
    const activeUsers = users.filter((u) => u.last_online && Date.now() - new Date(u.last_online).getTime() <= 7 * DAY);
    const share = (part, total) => (total ? Math.round((part / total) * 100) : 0);

    const statCards = [
        { title: 'Active Jobs', value: activeJobs, loading: jobsLoading, change: monthlyChange(jobs), icon: <WorkOutlineIcon />, color: 'primary' },
        { title: 'Applications', value: applicationsTotal, loading: applicationsLoading, change: monthlyChange(applications), icon: <AssignmentTurnedInIcon />, color: 'success' },
        { title: 'Registered Users', value: users.length, loading: usersLoading, change: monthlyChange(users), icon: <PeopleOutlineIcon />, color: 'warning' }
    ];

    const pipelineData = [
        { label: 'Jobs currently live', value: share(activeJobs, activeJobs + archivedJobs) },
        { label: 'New users (last 7 days)', value: share(newUsers.length, users.length) },
        { label: 'Users active (last 7 days)', value: share(activeUsers.length, users.length) }
    ];

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <AdminLayout pageTitle="Dashboard Insights">
            <Stack spacing={2.5}>
                <Box>
                    <Typography variant="h4" fontWeight={700} color="text.primary">
                        Hi {user?.first_name ?? 'Admin'} 👋
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Here's what's happening on the platform today.
                    </Typography>
                </Box>

                {/* Tabs */}
                <Paper elevation={0} sx={{ borderRadius: 3, px: 0.5 }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="Overview" />
                        <Tab label="New" />
                        <Tab label="Statistics" />
                    </Tabs>
                </Paper>

                {/* Tab Panels */}
                {tabValue === 0 && (
                    <>
                        <Grid container spacing={2}>
                            {statCards.map((stat) => (
                                <Grid item xs={12} md={4} key={stat.title}>
                                    <Paper
                                        elevation={0}
                                        sx={(theme) => {
                                            const c = theme.palette[stat.color].main;
                                            return {
                                                p: 2.25,
                                                height: '100%',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                background: `linear-gradient(135deg, ${alpha(c, 0.16)} 0%, ${alpha(c, 0.04)} 60%, transparent 100%)`,
                                                borderColor: alpha(c, 0.22),
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    width: 140,
                                                    height: 140,
                                                    right: -40,
                                                    top: -50,
                                                    borderRadius: '50%',
                                                    background: alpha(c, 0.18),
                                                    filter: 'blur(36px)',
                                                    pointerEvents: 'none',
                                                    transition: 'opacity .25s ease, transform .25s ease'
                                                },
                                                '&:hover': {
                                                    transform: 'translateY(-3px)',
                                                    borderColor: alpha(c, 0.45),
                                                    boxShadow: `0 14px 40px -14px ${alpha(c, 0.55)}`,
                                                    '&::after': { transform: 'scale(1.35)' }
                                                }
                                            };
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Avatar
                                                variant="rounded"
                                                sx={(theme) => ({
                                                    bgcolor: alpha(theme.palette[stat.color].main, 0.16),
                                                    color: theme.palette[stat.color].main,
                                                    width: 38,
                                                    height: 38,
                                                    borderRadius: 2.5,
                                                    '& svg': { fontSize: 20 }
                                                })}
                                            >
                                                {stat.icon}
                                            </Avatar>
                                            <Chip
                                                label={stat.change.label}
                                                color={stat.change.down ? 'error' : 'success'}
                                                size="small"
                                                variant="outlined"
                                                icon={stat.change.down ? <TrendingDownIcon sx={{ fontSize: 15 }} /> : <TrendingUpIcon sx={{ fontSize: 15 }} />}
                                                sx={{ height: 22, bgcolor: 'transparent' }}
                                            />
                                        </Stack>
                                        <Typography variant="h3" fontWeight={700} sx={{ mt: 1.75, lineHeight: 1.1 }}>
                                            {stat.loading ? '—' : stat.value}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            {stat.title}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={7}>
                                <Paper
                                    elevation={0}
                                    sx={{ p: 2.5, height: '100%', '&:hover': { borderColor: 'primary.main' } }}
                                >
                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Typography variant="h6" fontWeight={600} color="text.primary">
                                            Platform Snapshot
                                        </Typography>
                                        <Chip label="Live data" size="small" variant="outlined" />
                                    </Stack>
                                    <Divider sx={{ my: 1.75 }} />
                                    <Stack spacing={2.25}>
                                        {pipelineData.map((item) => (
                                            <Box key={item.label}>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography variant="body2" color="text.secondary">
                                                        {item.label}
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {item.value}%
                                                    </Typography>
                                                </Stack>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={item.value}
                                                    sx={{ mt: 0.9 }}
                                                />
                                            </Box>
                                        ))}
                                    </Stack>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <LastOnlineUsersWidget />
                            </Grid>
                        </Grid>
                    </>
                )}

                {tabValue === 1 && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={{ p: 2.5, height: '100%', '&:hover': { borderColor: 'primary.main' } }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
                                    New Users (Last 7 Days)
                                </Typography>
                                <Typography variant="h3" fontWeight={700} sx={{ my: 1.5 }}>
                                    {newUsers.length}
                                </Typography>
                                <List>
                                    {newUsers.slice(0, 10).map((user) => {
                                        const displayName =
                                            getProfileDisplayName(user.profile, user) ||
                                            user.username ||
                                            user.email ||
                                            'Member';
                                        const avatarSrc = getProfileAvatarUrl(user.profile);
                                        return (
                                        <ListItem key={user.id} disableGutters>
                                            <ListItemAvatar>
                                                <Avatar
                                                    src={avatarSrc || undefined}
                                                    alt={displayName}
                                                    sx={{
                                                        fontWeight: 700,
                                                        bgcolor: nameToAvatarColor(displayName),
                                                    }}
                                                >
                                                    {getProfileAvatarLetter(user.profile, user)}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={displayName}
                                                secondary={new Date(user.created_at || user.profile?.created_at).toLocaleDateString()}
                                            />
                                        </ListItem>
                                        );
                                    })}
                                </List>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={{ p: 2.5, height: '100%', '&:hover': { borderColor: 'primary.main' } }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
                                    Recent Jobs
                                </Typography>
                                <List>
                                    {jobs.slice(0, 10).map((job) => (
                                        <ListItem key={job.id} disableGutters>
                                            <ListItemText
                                                primary={job.title}
                                                secondary={`${job.company?.title || 'Unknown'} • ${new Date(job.created_at).toLocaleDateString()}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {tabValue === 2 && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={8}>
                            <OverviewStatsChart
                                jobs={jobs.length}
                                applications={applications.length}
                                salarySpecs={0}
                                invoices={0}
                                contracts={0}
                            />
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <Paper elevation={0} sx={{ p: 2.5, height: '100%', '&:hover': { borderColor: 'primary.main' } }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
                                    User Statistics
                                </Typography>
                                <Stack spacing={2} sx={{ mt: 2 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Total Users</Typography>
                                        <Typography variant="h5" fontWeight={700} color="text.primary">{users.length}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">New This Week</Typography>
                                        <Typography variant="h5" fontWeight={700} color="text.primary">{newUsers.length}</Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>
                        {jobs.length > 0 && (
                            <Grid item xs={12} lg={6}>
                                <MonthlyJobsChart jobs={jobs} />
                            </Grid>
                        )}
                        {applications.length > 0 && (
                            <Grid item xs={12} lg={6}>
                                <ApplicationsOverTimeChart applications={applications} />
                            </Grid>
                        )}
                    </Grid>
                )}
            </Stack>
        </AdminLayout>
    );
}

export default Dashboard;
